import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { CURRENCIES } from '../constants/currencies';
import { formatCurrency } from '../utils/formatters';
import { calculateCostPerMinute } from '../utils/calculations';

export default function SetupPage({ navigateTo, setParticipants, initialData, currency, onCurrencyChange }) {
  const [localParticipants, setLocalParticipants] = useState(
    initialData?.participantDetails || [{ id: 1, name: '', salaryType: 'annual', salaryValue: '', annualValue: '', hourlyValue: '' }]
  );

  useEffect(() => {
    if (initialData?.participantDetails) {
      return () => {};
    }
  }, [initialData]);

  const presets = [
    { label: 'Junior $50k', annual: 50000, hourly: 24.04 },
    { label: 'Mid $80k', annual: 80000, hourly: 38.46 },
    { label: 'Senior $120k', annual: 120000, hourly: 57.69 },
    { label: 'Executive $200k', annual: 200000, hourly: 96.15 }
  ];

  const addParticipant = () => {
    if (localParticipants.length < 20) {
      setLocalParticipants([...localParticipants, { id: Date.now(), name: '', salaryType: 'annual', salaryValue: '', annualValue: '', hourlyValue: '' }]);
    }
  };

  const removeParticipant = (id) => {
    if (localParticipants.length > 1) {
      setLocalParticipants(localParticipants.filter(p => p.id !== id));
    }
  };

  const updateParticipant = (id, field, value) => {
    setLocalParticipants(localParticipants.map(p => {
      if (p.id === id) {
        if (field === 'salaryType') {
          if (value === 'annual') {
            return { ...p, salaryType: 'annual', salaryValue: p.annualValue || '' };
          } else {
            return { ...p, salaryType: 'hourly', salaryValue: p.hourlyValue || '' };
          }
        } else if (field === 'annualValue') {
          return { ...p, annualValue: value, salaryValue: value, salaryType: 'annual' };
        } else if (field === 'hourlyValue') {
          return { ...p, hourlyValue: value, salaryValue: value, salaryType: 'hourly' };
        }
        return { ...p, [field]: value };
      }
      return p;
    }));
  };

  const applyPreset = (id, preset) => {
    setLocalParticipants(localParticipants.map(p => {
      if (p.id === id) {
        if (p.salaryType === 'hourly') {
          return { 
            ...p, 
            hourlyValue: preset.hourly, 
            salaryValue: preset.hourly,
            salaryType: 'hourly'
          };
        } else {
          return { 
            ...p, 
            annualValue: preset.annual, 
            salaryValue: preset.annual,
            salaryType: 'annual'
          };
        }
      }
      return p;
    }));
  };

  const startMeeting = () => {
    const valid = localParticipants.every(p => p.salaryValue && parseFloat(p.salaryValue) > 0);
    if (!valid) {
      alert('Please enter valid salary amounts for all participants');
      return;
    }
    setParticipants(localParticipants);
    navigateTo('tracker');
  };

  const costPerMinute = calculateCostPerMinute(
    localParticipants.filter(p => p.salaryValue && parseFloat(p.salaryValue) > 0)
  );

  const allParticipantsValid = localParticipants.every(p => p.salaryValue && parseFloat(p.salaryValue) > 0);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Add Participant Details</h1>
            <p className="text-gray-600">Enter salary information for each meeting participant</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
            <select
              value={currency}
              onChange={(e) => onCurrencyChange(e.target.value)}
              className="px-4 py-2 bg-white border-2 border-purple-300 rounded-lg shadow-sm text-sm font-medium focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              {Object.entries(CURRENCIES).map(([code, info]) => (
                <option key={code} value={code}>
                  {info.symbol} {code} - {info.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-700">
            <span className="font-semibold text-green-700">✨ Privacy First:</span> All data stays in your browser. No account, no tracking, no servers.
          </p>
        </div>

        <div className="space-y-6">
          {localParticipants.map((participant, index) => (
            <div key={participant.id} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg">Participant {index + 1}</h3>
                {localParticipants.length > 1 && (
                  <button
                    onClick={() => removeParticipant(participant.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name (optional)</label>
                  <input
                    type="text"
                    value={participant.name}
                    onChange={(e) => updateParticipant(participant.id, 'name', e.target.value)}
                    placeholder={`Person ${index + 1}`}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Salary Input</label>
                  <div className="space-y-3">
                    <label className="block">
                      <div className="flex items-center mb-1">
                        <input
                          type="radio"
                          checked={participant.salaryType === 'annual'}
                          onChange={() => updateParticipant(participant.id, 'salaryType', 'annual')}
                          className="mr-2"
                        />
                        <span>Annual Salary:</span>
                      </div>
                      <input
                        type="number"
                        value={participant.annualValue}
                        onChange={(e) => updateParticipant(participant.id, 'annualValue', e.target.value)}
                        onFocus={() => updateParticipant(participant.id, 'salaryType', 'annual')}
                        placeholder="80000"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </label>
                    <label className="block">
                      <div className="flex items-center mb-1">
                        <input
                          type="radio"
                          checked={participant.salaryType === 'hourly'}
                          onChange={() => updateParticipant(participant.id, 'salaryType', 'hourly')}
                          className="mr-2"
                        />
                        <span>Hourly Rate:</span>
                      </div>
                      <input
                        type="number"
                        value={participant.hourlyValue}
                        onChange={(e) => updateParticipant(participant.id, 'hourlyValue', e.target.value)}
                        onFocus={() => updateParticipant(participant.id, 'salaryType', 'hourly')}
                        placeholder="45"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">OR Quick Presets:</label>
                  <div className="grid grid-cols-2 gap-2">
                    {presets.map(preset => (
                      <button
                        key={preset.label}
                        onClick={() => applyPreset(participant.id, preset)}
                        className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
                      >
                        <div className="font-semibold">{preset.label.split(' ')[0]}</div>
                        <div className="text-xs mt-1">
                          {participant.salaryType === 'hourly' 
                            ? `${CURRENCIES[currency].symbol}${preset.hourly}/hr`
                            : `${CURRENCIES[currency].symbol}${(preset.annual / 1000).toFixed(0)}k/yr`
                          }
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Presets show values based on your selected input type (annual/hourly)
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-4">
          <button
            onClick={addParticipant}
            disabled={localParticipants.length >= 20}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Add Another Participant
          </button>
        </div>

        <div className="mt-8 p-6 bg-purple-50 rounded-lg">
          <p className="text-lg font-semibold text-gray-900">
            Estimated cost: <span className="text-purple-600">{formatCurrency(costPerMinute, currency)}/minute</span>
          </p>
        </div>

        <div className="mt-8 flex gap-4">
          <button
            onClick={() => navigateTo('home')}
            className="px-6 py-3 border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50"
          >
            ← Back
          </button>
          <button
            onClick={startMeeting}
            disabled={!allParticipantsValid}
            className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-500"
          >
            Start Meeting
          </button>
        </div>
      </div>
    </div>
  );
}